import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, LoaderCircle, Maximize2, ScanLine, X } from 'lucide-react';
import { fittedFrameSize, videoFrameTimes } from './video-frame-utils.js';

function waitForVideo(video, eventName, timeout = 8000) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      video.removeEventListener(eventName, ready);
      video.removeEventListener('error', failed);
      if (error) reject(error); else resolve();
    };
    const ready = () => finish();
    const failed = () => finish(new Error('视频读取失败'));
    const timer = window.setTimeout(() => finish(new Error('视频读取超时')), timeout);
    video.addEventListener(eventName, ready, { once: true });
    video.addEventListener('error', failed, { once: true });
  });
}

async function seekVideo(video, time) {
  if (Math.abs(Number(video.currentTime || 0) - time) < 0.01 && video.readyState >= 2) return;
  video.currentTime = time;
  await waitForVideo(video, 'seeked', 6000);
}

function canvasBlob(canvas) {
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob) => blob ? resolve(blob) : reject(new Error('画面编码失败')),
    'image/jpeg',
    0.86,
  ));
}

export default function VideoFramePreview({ url, onPreview, onSelectFrame, onNotify }) {
  const [frames, setFrames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const urlsRef = useRef([]);

  const clear = useCallback(() => {
    urlsRef.current.forEach((item) => URL.revokeObjectURL(item));
    urlsRef.current = [];
    setFrames([]);
    setSelectedTime(null);
    setHoveredTime(null);
  }, []);

  useEffect(() => {
    clear();
    return clear;
  }, [clear, url]);

  const capture = async () => {
    if (!url || busy) return;
    setBusy(true);
    clear();
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    try {
      video.src = url;
      video.load();
      await waitForVideo(video, 'loadedmetadata');
      if (video.readyState < 2) await waitForVideo(video, 'loadeddata');
      const size = fittedFrameSize(video.videoWidth, video.videoHeight);
      const canvas = document.createElement('canvas');
      canvas.width = size.width;
      canvas.height = size.height;
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('浏览器无法读取视频画面');
      const captured = [];
      for (const time of videoFrameTimes(video.duration)) {
        await seekVideo(video, time);
        context.drawImage(video, 0, 0, size.width, size.height);
        const objectUrl = URL.createObjectURL(await canvasBlob(canvas));
        urlsRef.current.push(objectUrl);
        captured.push({
          url: objectUrl,
          time,
          width: size.width,
          height: size.height,
          phase: time < 2 ? 'start' : 'end',
        });
      }
      setFrames(captured);
      onNotify?.(`已抽取 ${captured.length} 帧`);
    } catch (error) {
      clear();
      onNotify?.(error.message || '抽帧失败', 'error');
    } finally {
      video.removeAttribute('src');
      video.load();
      setBusy(false);
    }
  };

  return <section className="video-frame-tool nodrag nowheel" aria-label="视频抽帧预览">
    <div className="video-frame-tool__bar">
      <button type="button" disabled={!url || busy} onClick={capture}>
        {busy ? <LoaderCircle className="spin" size={13} /> : <ScanLine size={13} />}
        {busy ? '正在抽帧' : '抽帧预览'}
      </button>
      {frames.length > 0 && <button type="button" className="is-clear" onClick={clear}><X size={12} />清空</button>}
    </div>
    {frames.length > 0 && <div className="video-frame-groups" aria-label="前后两秒抽帧结果">
      {[
        ['start', '前 2 秒'],
        ['end', '后 2 秒'],
      ].map(([phase, label]) => {
        const group = frames.filter((frame) => frame.phase === phase);
        if (!group.length) return null;
        return <section className="video-frame-group" key={phase}>
          <h4>{label}<small>每 0.5 秒</small></h4>
          <div className="video-frame-grid">
            {group.map((frame) => <button
        type="button"
        key={`${frame.url}-${frame.time}`}
        className={selectedTime === frame.time ? 'is-selected' : ''}
        title={`选择 ${frame.time.toFixed(2)} 秒画面并加入参考图`}
        draggable="true"
        onDragStart={(event) => {
          event.stopPropagation();
          event.dataTransfer.effectAllowed = 'copy';
          event.dataTransfer.setData('application/x-nebula-video-frame', JSON.stringify({ url: frame.url, time: frame.time, width: frame.width, height: frame.height }));
        }}
        onDoubleClick={() => onPreview?.({ kind: 'image', url: frame.url, name: `视频帧 ${frame.time.toFixed(2)}s` })}
        onClick={() => {
          setSelectedTime(frame.time);
          onSelectFrame?.(frame);
        }}
        onMouseEnter={() => setHoveredTime(frame.time)}
        onMouseLeave={() => setHoveredTime(null)}
      >
        <img src={frame.url} alt="" draggable="false" />
        <span><ImageIcon size={10} />{frame.time.toFixed(2)}s</span>
        {hoveredTime === frame.time && <b className="video-frame-hover-preview" aria-hidden="true">
          <img src={frame.url} alt="" draggable="false" />
          <small>{frame.time.toFixed(2)}s</small>
        </b>}
        <i
          className="video-frame-grid__zoom"
          role="button"
          tabIndex={0}
          aria-label={`放大查看 ${frame.time.toFixed(2)} 秒画面`}
          title="放大查看"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPreview?.({ kind: 'image', url: frame.url, name: `视频帧 ${frame.time.toFixed(2)}s` });
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onPreview?.({ kind: 'image', url: frame.url, name: `视频帧 ${frame.time.toFixed(2)}s` });
            }
          }}
        ><Maximize2 size={12} /></i>
            </button>)}
          </div>
        </section>;
      })}
    </div>}
  </section>;
}
